import type {
  DynamicRegenSync,
  RiskAssessmentInput,
  SnapshotInvalidation,
  SubmitInstanceRiskAssessmentResponse,
} from "@repo/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiErrorCode } from "@/lib/api/_client";
import {
  getPublicInstanceRiskAssessmentValue,
  getPublicRiskAssessmentValue,
  savePublicInstanceRiskAssessmentPartial,
  savePublicRiskAssessmentPartial,
  submitPublicInstanceRiskAssessment,
  submitPublicRiskAssessment,
} from "@/lib/api/public-flow/risk-assessment";
import { useSessionToken } from "@/lib/context/public-session-context";
import { log } from "@/lib/logger/browser-logger";
import { useInvalidatePublicPortfolioSnapshots } from "./use-goal-snapshots";
import { invalidatePublicSuitabilityQueries, publicGoalQueryKeys } from "./use-goals";
import { useInvalidatePublicDynamicSync } from "./use-portfolios";
import { publicFlowCompletionQueryKey } from "./use-public-flow-completion";

const keys = {
  flowInstanceValue: (goalId: string | null) =>
    ["publicFlow", "risk-assessment", goalId, "flow-instance-value"] as const,
  /**
   * QL-212 — the client-level row's key.
   *
   * The literal `"instance"` segment matters: `flowInstanceValue` puts a possibly
   * `null` goalId in that same position, so a bare `null` here would collide with
   * a disabled per-goal query.
   */
  instanceValue: () =>
    ["publicFlow", "risk-assessment", "instance", "flow-instance-value"] as const,
  /**
   * The instance-wide snapshot aggregates (`GlobalCostTable`, `GlobalExpectedValue`),
   * which render on the very global pages this component sits on. Their key shape
   * is set by `useInstanceCostSnapshot` / `useInstanceExpectedValueSnapshot` in the
   * shared snapshot hooks, not by this file.
   */
  instanceSnapshot: (flowInstanceId: string, type: "instanceCost" | "instanceExpectedValue") =>
    ["publicFlow", "instance", flowInstanceId, "snapshot", type] as const,
};

export function usePublicRiskAssessmentValue(goalId: string | null) {
  const token = useSessionToken();
  const { data, error, isLoading } = useQuery({
    queryKey: keys.flowInstanceValue(goalId),
    queryFn: () => getPublicRiskAssessmentValue(token, goalId!),
    enabled: !!token && !!goalId,
  });
  return {
    inputData: data?.inputData ?? null,
    calculatedData: data?.calculatedData ?? null,
    error,
    isLoading,
  };
}

export function useSubmitPublicRiskAssessment() {
  const token = useSessionToken();
  const queryClient = useQueryClient();
  const invalidatePortfolioSnapshots = useInvalidatePublicPortfolioSnapshots();
  const invalidateDynamicSync = useInvalidatePublicDynamicSync();
  return useMutation({
    mutationFn: ({
      goalId,
      data,
      isComplete,
      lang,
    }: {
      goalId: string;
      data: RiskAssessmentInput & { score: number };
      isComplete: boolean;
      /**
       * QL-212 — the flow's language. Without it the server falls back to
       * `en_GB` and stores English option names against a client who answered
       * in their own language.
       */
      lang?: string;
    }) =>
      isComplete
        ? submitPublicRiskAssessment(token, goalId, data, lang)
        : savePublicRiskAssessmentPartial(token, goalId, data),
    onSuccess: (result, { goalId, isComplete }) => {
      if (result.success && result.inputData !== undefined) {
        queryClient.setQueryData(keys.flowInstanceValue(goalId), {
          inputData: result.inputData,
          calculatedData: result.calculatedData,
        });
      }
      // When risk assessment is completed, backend matches portfolio + regenerates snapshots.
      if (result.success && isComplete) {
        queryClient.invalidateQueries({ queryKey: publicGoalQueryKeys.goals() });
        // QP-150 — process refreshHints for portfolio-scoped snapshot invalidation.
        const hints = (result as { refreshHints?: SnapshotInvalidation[] }).refreshHints;
        if (hints) {
          for (const hint of hints) {
            invalidatePortfolioSnapshots(hint.portfolioId, hint);
          }
        }
        // QP-185 — the new score regenerated dynamic portfolios.
        invalidateDynamicSync((result as { dynamicSync?: DynamicRegenSync }).dynamicSync);
        queryClient.invalidateQueries({ queryKey: publicFlowCompletionQueryKey });
        // QL-10 — refresh the builder's suitability verdicts explicitly. The
        // goals invalidation above happens to prefix-match the suitability keys today,
        // but that is incidental; the other suitability-feeding components call this too.
        invalidatePublicSuitabilityQueries(queryClient);
      }
    },
  });
}

// ─── Instance-level (client-level) capture — QL-212 ──────────────────────────

/**
 * Read the client-level risk answer — the `goalId IS NULL` row. Seeds the widget
 * on a global page, the way `usePublicRiskAssessmentValue` seeds it on a goal
 * page. The flow instance is identified by the session, so there is nothing to
 * key on but the token.
 */
export function usePublicInstanceRiskAssessmentValue(riskModelId: string | null) {
  const token = useSessionToken();
  const { data, error, isLoading } = useQuery({
    queryKey: keys.instanceValue(),
    queryFn: () => getPublicInstanceRiskAssessmentValue(token),
    // A flow with no risk model has no assessment to read; the service does not
    // check, so gate here rather than firing a pointless request.
    enabled: !!token && !!riskModelId,
  });
  return {
    inputData: data?.inputData ?? null,
    calculatedData: data?.calculatedData ?? null,
    error,
    isLoading,
  };
}

/**
 * Save or submit the client-level answer from the public flow.
 *
 * Same autosave contract as the per-goal hook (partial while incomplete, submit
 * once complete, no submit button); what differs is the blast radius. A global
 * submit scores EVERY goal, so the invalidation fans out over the goals the
 * server says it touched rather than the single goal the caller named.
 */
export function useSubmitPublicInstanceRiskAssessment(
  flowInstanceId: string,
  /** Translated, client-facing toast copy in the flow's language. */
  messages: { saveFailed: string; matrixGap: string },
) {
  const token = useSessionToken();
  const queryClient = useQueryClient();
  const invalidatePortfolioSnapshots = useInvalidatePublicPortfolioSnapshots();
  const invalidateDynamicSync = useInvalidatePublicDynamicSync();

  return useMutation({
    mutationFn: ({
      data,
      isComplete,
      lang,
    }: {
      data: RiskAssessmentInput;
      isComplete: boolean;
      lang?: string;
    }) =>
      isComplete
        ? submitPublicInstanceRiskAssessment(token, data, lang)
        : savePublicInstanceRiskAssessmentPartial(token, data),
    onError: (error) => {
      // Autosave has no UI of its own, so a rejected write would otherwise fail
      // in total silence — tell the client, and log it. The server's message is
      // never shown here: it is English and written for the advisor. A fixed id
      // keeps repeated autosave failures from stacking toasts.
      log.error({ err: error }, "Failed to save client-level risk assessment");
      toast.error(
        apiErrorCode(error) === "risk_matrix_gap" ? messages.matrixGap : messages.saveFailed,
        { id: "public-instance-risk-assessment-save" },
      );
    },
    onSuccess: (result, { isComplete }) => {
      if (!result.success) return;
      if (result.inputData !== undefined) {
        queryClient.setQueryData(keys.instanceValue(), {
          inputData: result.inputData,
          calculatedData: result.calculatedData,
        });
      }
      // Every goal-keyed read is the MERGED one, so any stored client-level
      // answer (partial saves included) changes what each goal's page shows.
      // A partial save names no goals, so match the key shape rather than the
      // fan-out's outcomes; skip the instance row seeded above.
      queryClient.invalidateQueries({
        predicate: ({ queryKey: k }) =>
          k[0] === "publicFlow" &&
          k[1] === "risk-assessment" &&
          k[2] !== "instance" &&
          k[3] === "flow-instance-value",
      });
      if (!isComplete) return;

      // ["publicFlow","goals"] is a PREFIX of the per-goal snapshot and
      // suitability keys, so this one call already refreshes every goal's
      // snapshots. No per-goal snapshot loop is needed, and none would be more
      // precise than this.
      queryClient.invalidateQueries({ queryKey: publicGoalQueryKeys.goals() });

      // The instance-wide aggregates are NOT under that prefix, and a fan-out
      // that re-resolves every goal's model portfolios moves them — they are
      // very likely on the same global page as this widget.
      queryClient.invalidateQueries({
        queryKey: keys.instanceSnapshot(flowInstanceId, "instanceCost"),
      });
      queryClient.invalidateQueries({
        queryKey: keys.instanceSnapshot(flowInstanceId, "instanceExpectedValue"),
      });

      // QP-150 — portfolio-scoped snapshots, named by the server. Under QL-212's
      // D9 gate these name only what actually changed, so re-submitting the same
      // answers invalidates nothing.
      const hints = (result as SubmitInstanceRiskAssessmentResponse).refreshHints;
      if (hints) {
        for (const hint of hints) invalidatePortfolioSnapshots(hint.portfolioId, hint);
      }
      // QP-185 — the new score regenerated the dynamic portfolios.
      invalidateDynamicSync((result as SubmitInstanceRiskAssessmentResponse).dynamicSync);

      queryClient.invalidateQueries({ queryKey: publicFlowCompletionQueryKey });
      // QL-10 — every goal's risk level and score are suitability inputs, and a
      // global submit moves all of them at once.
      invalidatePublicSuitabilityQueries(queryClient);
    },
  });
}
